import * as React from 'react';
import {
  CheckIcon,
  CopyIcon,
  FileTextIcon,
} from 'lucide-react';
import {
  Card,
  Button,
  Tabs,
  TabsContent,
} from '@tinct/web-components';
import { cn } from '@tinct/core-utils';

type InstallationMethod =
  | 'javascript'
  | 'gtm'
  | 'webflow'
  | 'framer'
  | 'shopify'
  | 'wordpress';

type InstallationStep = {
  step: number;
  description: string;
};

export type SnippetInstallationCardProps = {
  className?: string;
  snippetId?: string;
  snippetUrlPrefix?: string;
  onCopyCode?: (code: string) => void;
};

const INSTALLATION_STEPS: Partial<
  Record<InstallationMethod, InstallationStep[]>
> = {
  javascript: [
    { step: 1, description: 'Copy the code snippet above' },
    {
      step: 2,
      description:
        "Paste it in your website's HTML, just before the closing </head> tag",
    },
    { step: 3, description: 'Deploy/publish your changes to production' },
    { step: 4, description: 'Click "Verify Installation" below to confirm' },
  ],
  gtm: [
    { step: 1, description: 'Copy the code snippet above' },
    {
      step: 2,
      description:
        'In Google Tag Manager, create a new tag of type "Custom HTML" and paste the code',
    },
    {
      step: 3,
      description:
        'Set the trigger to "Initialization - All Pages" so the snippet loads as early as possible',
    },
    { step: 4, description: 'Save the tag, then submit and publish your container' },
    { step: 5, description: 'Click "Verify Installation" below to confirm' },
  ],
};

const DOCUMENTATION_URL_FALLBACK =
  'https://help.tinct.ai/en/help/collections/collections/9841563-snippet-installation';

const DOCUMENTATION_URLS: Partial<Record<InstallationMethod, string>> = {
  javascript:
    'https://help.tinct.ai/en/help/articles/3797445-installing-via-javascript-html',
  gtm: 'https://help.tinct.ai/en/help/articles/4980392-snippet-installation-google-tag-manager-gtm',
};

function StepBadge({ step }: { step: number }) {
  return (
    <span className="flex size-5 min-w-5 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
      {step}
    </span>
  );
}

function CodeBlock({
  code,
  language = 'HTML',
  onCopy,
}: {
  code: string;
  language?: string;
  onCopy?: () => void;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    onCopy?.();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-md">
      <div className="flex h-12 items-center justify-between border-b border-neutral-600 bg-foreground px-6 py-2">
        <span className="text-sm text-muted-foreground">{language}</span>
        <Button variant="default" size="default" onClick={handleCopy}>
          {copied ? (
            <CheckIcon className="size-4" />
          ) : (
            <CopyIcon className="size-4" />
          )}
          {copied ? 'Copied!' : 'Copy code'}
        </Button>
      </div>
      <div className="flex min-h-12 items-center overflow-x-auto bg-foreground px-6 py-2">
        <pre className="text-base text-primary-foreground">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  );
}

export function SnippetInstallationCard({
  className,
  snippetId,
  snippetUrlPrefix = '',
  onCopyCode,
}: SnippetInstallationCardProps) {
  const [activeTab, setActiveTab] =
    React.useState<InstallationMethod>('javascript');

  const snippetUrl = `${snippetUrlPrefix}?snippet_id=${snippetId ?? ''}`;

  const getSnippetCode = (method: InstallationMethod): string => {
    switch (method) {
      case 'javascript':
        return `<script src="${snippetUrl}"  referrerpolicy="no-referrer-when-downgrade"></script>`;
      case 'gtm':
        return `<script>
  (function () {
    var s = document.createElement('script');
    s.src = '${snippetUrl}';
    s.async = true;
    s.referrerPolicy = 'no-referrer-when-downgrade';
    document.head.appendChild(s);
  })();
</script>`;
      case 'webflow':
        return `<!-- Webflow embed code: ${snippetId} -->`;
      case 'framer':
        return `<!-- Framer code override: ${snippetId} -->`;
      case 'shopify':
        return `<!-- Shopify theme snippet: ${snippetId} -->`;
      case 'wordpress':
        return `<!-- WordPress plugin shortcode: ${snippetId} -->`;
      default:
        return '';
    }
  };

  const handleCopyCode = () => {
    const code = getSnippetCode(activeTab);
    onCopyCode?.(code);
  };

  return (
    <Card className={cn('gap-0 py-0', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6">
        <h3 className="text-xl font-semibold tracking-tight text-foreground">
          Installation method
        </h3>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as InstallationMethod)}
        className="gap-0"
      >
        <div className="flex items-start border-b border-border px-3">
          <div className="flex items-center">
            {(
              [
                { value: 'javascript', label: 'Javascript' },
                { value: 'gtm', label: 'Google Tag Manager' },
                // { value: 'webflow', label: 'Webflow' },
                // { value: 'framer', label: 'Framer' },
                // { value: 'shopify', label: 'Shopify' },
                // { value: 'wordpress', label: 'WordPress' },
              ] as const
            ).map((tab) => {
              const isActive = activeTab === tab.value;
              return (
                <div
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={cn(
                    'flex items-center justify-center py-1.5 cursor-pointer',
                    {
                      'border-b-2 border-primary': isActive,
                      'border-b-2 border-transparent': !isActive,
                    }
                  )}
                >
                  <div
                    className={cn(
                      'flex items-center justify-center px-2.5 py-2 rounded-md transition-colors',
                      !isActive && 'hover:bg-accent'
                    )}
                  >
                    <span
                      className={cn(
                        'text-sm whitespace-nowrap transition-colors',
                        isActive
                          ? 'text-foreground'
                          : 'text-muted-foreground hover:text-accent-foreground'
                      )}
                    >
                      {tab.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6">
          {/* Code Block */}
          <TabsContent value="javascript" className="mt-0">
            <CodeBlock
              code={getSnippetCode('javascript')}
              onCopy={handleCopyCode}
            />
          </TabsContent>
          <TabsContent value="gtm" className="mt-0">
            <CodeBlock code={getSnippetCode('gtm')} onCopy={handleCopyCode} />
          </TabsContent>
          {/* <TabsContent value="webflow" className="mt-0">
            <CodeBlock
              code={getSnippetCode('webflow')}
              onCopy={handleCopyCode}
            />
          </TabsContent>
          <TabsContent value="framer" className="mt-0">
            <CodeBlock
              code={getSnippetCode('framer')}
              onCopy={handleCopyCode}
            />
          </TabsContent>
          <TabsContent value="shopify" className="mt-0">
            <CodeBlock
              code={getSnippetCode('shopify')}
              onCopy={handleCopyCode}
            />
          </TabsContent>
          <TabsContent value="wordpress" className="mt-0">
            <CodeBlock
              code={getSnippetCode('wordpress')}
              onCopy={handleCopyCode}
            />
          </TabsContent> */}

          {/* Installation Steps */}
          <div className="flex flex-col gap-3">
            <h4 className="text-base font-semibold text-foreground">
              Installation steps
            </h4>
            {(INSTALLATION_STEPS[activeTab] ?? []).map((item) => (
              <div key={item.step} className="flex items-start gap-3">
                <StepBadge step={item.step} />
                <span className="text-sm text-muted-foreground">
                  {item.description}
                </span>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" asChild>
              <a
                href={
                  DOCUMENTATION_URLS[activeTab] ?? DOCUMENTATION_URL_FALLBACK
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileTextIcon className="size-4" />
                View documentation
              </a>
            </Button>
          </div>
        </div>
      </Tabs>
    </Card>
  );
}
